import React, { useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import styled from 'styled-components';
import { FormPrevious } from 'grommet-icons';
import { Alert as AlertIcon } from 'grommet-icons/icons';
import { BeaconChainValidator, Props } from './types';
import { Text } from '../../components/Text';
import { web3ReactInterface } from '../ConnectWallet';
import WalletConnectModal from './components/WalletConnectModal';
import ValidatorTable from './components/ValidatorTable';
import TopupPage from './components/TopupPage';
import Spinner from '../../components/Spinner';
import { PageTemplate } from '../../components/PageTemplate';
import {
  PRICE_PER_VALIDATOR,
  EJECTION_PRICE,
  TICKER_NAME,
} from '../../utils/envVars';
import { Alert } from '../../components/Alert';
import { Link } from '../../components/Link';
import { Button } from '../../components/Button';
import useValidators from '../../hooks/useValidators';

const Arrow = styled(props => <FormPrevious {...props} />)`
  position: absolute;
  left: 0;
  color: ${p => p.theme.blue.medium};
`;

const SubTextContainer = styled.div``;

const BackText = styled(Text)`
  margin-top: -25px;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
  position: relative;
  padding-left: 25px;
`;

const FakeLink = styled.span`
  color: blue;
  text-decoration: underline;
  cursor: pointer;
  display: inline;
`;

const ButtonLink = styled(FakeLink)`
  text-decoration: none;
  width: fit-content;
  button {
    padding: 16px 32px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  @media screen and (max-width: 518px) {
    span,
    button {
      width: 100%;
    }
  }
`;

const _TopUpPage: React.FC<Props> = () => {
  const {
    account,
    active,
    chainId,
    deactivate,
  }: web3ReactInterface = useWeb3React<Web3Provider>();
  const {
    validators,
    loading,
    setLoading,
    validatorLoadError,
    showDepositVerificationWarning,
  } = useValidators(account, chainId, active);
  const { formatMessage } = useIntl();

  const [
    selectedValidator,
    setSelectedValidator,
  ] = useState<BeaconChainValidator | null>(null);

  const handleConnect = useCallback((): void => {
    setLoading(true);
    deactivate();
  }, [setLoading, deactivate]);

  const handleModalClose = useCallback((): void => {
    setLoading(false);
  }, [setLoading]);

  const topUpPageContent = useMemo(() => {
    if (loading) {
      return <Spinner className="mt40" />;
    }

    if (!active) {
      return (
        <ButtonContainer>
          <ButtonLink onClick={handleConnect}>
            <Button
              rainbow
              label={formatMessage({ defaultMessage: 'Connect wallet' })}
            />
          </ButtonLink>
        </ButtonContainer>
      );
    }
    if (validatorLoadError) {
      return (
        <Alert variant="warning" className="my10">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <AlertIcon color="redLight" />
            <Text className="ml10">
              <FormattedMessage defaultMessage="There was an error loading your validator information from Beaconcha.in" />
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Text className="mt20">
              <FormattedMessage
                defaultMessage="You can {reloadYourWallet} to try again, or refresh the page. If issue persists, let us know on {github}."
                values={{
                  reloadYourWallet: (
                    <FakeLink onClick={deactivate}>
                      <FormattedMessage defaultMessage="reload your wallet" />
                    </FakeLink>
                  ),
                  github: (
                    <Link
                      inline
                      primary
                      to="https://github.com/lukso-network/network-deposit-launchpad/issues/new"
                    >
                      GitHub
                    </Link>
                  ),
                }}
                description="{reloadYourWallet} is a link labeled 'reload your wallet'"
              />
            </Text>
          </div>
        </Alert>
      );
    }

    if (selectedValidator) {
      return <TopupPage validator={selectedValidator} />;
    }

    return (
      <>
        {showDepositVerificationWarning && (
          <Alert variant="warning" className="my10">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <AlertIcon color="redLight" />
              <Text className="ml10">
                <FormattedMessage
                  defaultMessage="You may have an {TICKER_NAME} deposit that was accepted but is being
                    validated on chain. It will be available here when beaconcha.in
                    has confirmed 2048 blocks."
                  values={{ TICKER_NAME }}
                />
              </Text>
            </div>
          </Alert>
        )}
        <ValidatorTable
          validators={validators
            .filter(
              validator =>
                validator.status === 'active_ongoing' ||
                validator.status === 'active_offline'
            )
            .sort((l, r) => (l.balance <= r.balance ? -1 : 1))}
          setSelectedValidator={setSelectedValidator}
          handleConnect={handleConnect}
        />
      </>
    );
  }, [
    loading,
    validatorLoadError,
    selectedValidator,
    active,
    validators,
    showDepositVerificationWarning,
    deactivate,
    formatMessage,
    handleConnect,
  ]);

  return (
    <>
      {/* the wallet connect modal controls it's own display, so it is always rendered here */}
      <WalletConnectModal
        loading={loading}
        handleModalClose={handleModalClose}
      />

      <PageTemplate
        title={formatMessage({ defaultMessage: 'Top up a validator' })}
      >
        {/* render a "back button" if there's a selected validator */}
        {selectedValidator && (
          <BackText
            onClick={() => setSelectedValidator(null)}
            color="blueMedium"
          >
            <Arrow />
            <FormattedMessage defaultMessage="All validators" />
          </BackText>
        )}

        <SubTextContainer className="mt20">
          <Text className="mt10">
            <FormattedMessage
              defaultMessage="You may need to top up your validator's balance for two important reasons. If your validator's effective balance is below {PRICE_PER_VALIDATOR} {TICKER_NAME} you won't be earning your full staker rewards. And if it drops as low as {EJECTION_PRICE} {TICKER_NAME} the system will eject your validator."
              values={{ PRICE_PER_VALIDATOR, TICKER_NAME, EJECTION_PRICE }}
            />
          </Text>
          <Alert variant="info" className="my20">
            <FormattedMessage
              defaultMessage="{PRICE_PER_VALIDATOR} {TICKER_NAME} is the maximum effective validator balance. This means you won't earn more rewards if your validator's balance goes above {PRICE_PER_VALIDATOR}. However you will earn less if it dips below {PRICE_PER_VALIDATOR}."
              values={{ PRICE_PER_VALIDATOR, TICKER_NAME }}
            />{' '}
            <Link
              inline
              primary
              to="https://www.attestant.io/posts/understanding-validator-effective-balance/"
            >
              <FormattedMessage defaultMessage="More on effective balance." />
              <br />
            </Link>
            <FormattedMessage
              defaultMessage="After the Pectra fork you can stake up to 2048 {TICKER_NAME} with compounding validators."
              values={{ TICKER_NAME }}
            />{' '}
            <Link
              inline
              primary
              to="https://docs.ethstaker.org/upgrades/pectra-features"
            >
              <FormattedMessage defaultMessage="More on compounding validators" />
            </Link>
          </Alert>
        </SubTextContainer>

        {/* the main content for the topup page */}
        {topUpPageContent}
      </PageTemplate>
    </>
  );
};

export const TopUpPage = _TopUpPage;
